
import * as AdaptiveCards from 'adaptivecards';
import { Input } from 'adaptivecards';
import { IVersionedChoice, PropertySheet, PropertySheetCategory, PropertySheetEntry } from 'adaptivecards-designer';
import _ from 'lodash';
import { AppStateValue } from '../../../AppState';
import { CustomPropertyDataType_LABELS } from '../../../custom-properties/utils/constants';
import { ICustomProperty } from '../../../shared/interfaces/customproperties.interface';
import { Comment } from '../../../shared/interfaces/ticket.interface';
import { TicketProps } from '../../../shared/interfaces/ticketproperties.interface';
import { platformService } from '../../../shared/services/platform.service';
import { AdaptiveCardTexts, FILTERS_STRINGS, TEMPLATE_DROPDOWN_RETURN_VALUE } from '../../../shared/utils/constants';
import { filterPlatformUsersInRole } from '../../../tikit/ticketHelper';
import { AdaptiveCardUploadProps } from '../../AdaptiveCardElement';
import { AddComment } from '../AddComment';
import { Assignment, AssignmentPeer } from '../Assignment';
import { ListProperty } from '../CustomProperties/ListProperty';
import { IDataSource } from '../DataSource/DataSourceTable';
import { DueDate } from '../Date/DueDate';
import { ResolutionDate } from '../Date/ResolutionDate';
import { AffectedUsersPeoplePicker } from '../PeoplePicker/AffectedUsersPeoplePicker';
import { CardPeoplePicker } from '../PeoplePicker/CardPeoplePicker';
import { RequesterPeoplePicker } from '../PeoplePicker/RequesterPeoplePicker';
import { GroupPicker, GroupPickerPeer } from '../Pickers/GroupPicker';
import { TeamPicker, TeamPickerPeer } from '../Pickers/TeamPicker';
import { StatusOfMyRequests } from '../StatusOfMyRequests';
import { SubmitRequest } from '../SubmitRequest';
import { CollaboratorsTypeahead } from '../Typeahead/CollaboratorsTypeahead';
import { TagsTypeahead, TagsTypeaheadPeer } from '../Typeahead/TagsTypeahead';
import { UploadFile } from '../UploadFile';
import { CategoryPicker, CategoryPickerPeer } from './../Pickers/CategoryPicker';
import { PriorityPicker, PriorityPickerPeer } from './../Pickers/PriorityPicker';
import { StatusPicker, StatusPickerPeer } from './../Pickers/StatusPicker';
import { TicketTypePicker, TicketTypePickerPeer } from './../Pickers/TicketTypePicker';
import { RequestTitle } from './../RequestTitle';

export async function initializeCustomFieldConfigurations(currentState: AppStateValue) {
    initializeDataSource();
    initializeAssignment(currentState);
}

export async function initializeAssignment(currentState: AppStateValue) {
    const teams = currentState?.teams;
    const teamChoices = teams.map(t => {
        return { name: t.FriendlyName, value: t.TeamsAadObjectId} as IVersionedChoice;
    });
    const groups = currentState?.supportgroups;
    const groupChoices = groups.map(t => {
        return { name: t.Name, value: t.Id.toString()} as IVersionedChoice;
    });

    Assignment.teamChoices = teams;
    Assignment.groupChoices = groups;
    Assignment.defaultTeam = currentState?.defaultTeam;
    AssignmentPeer.updateChoices(teamChoices, groupChoices);
}

export const initialPickerChoices = [
{
    "name": "(default)",
    "value": "0"
}
] as IVersionedChoice[];

export async function initializeDataSource() {
    const api = new platformService();

    const results = await Promise.all([
        api.getDataSources('?$orderby=Name')
    ]);

    const dataSources: IDataSource[] = results[0]?.data?.value ?? [];
    
    const getPickerOptions = (type: string) => {
        return initialPickerChoices
        .concat(dataSources.filter(d => d.EntityType == type)
        .map(x => { return { "name": x.Name, "value": x.Id.toString()} as IVersionedChoice}))
    }
    
    StatusPickerPeer.updateChoices(getPickerOptions("Status"));
    TicketTypePickerPeer.updateChoices(getPickerOptions("TicketType"));
    CategoryPickerPeer.updateChoices(getPickerOptions("Category"));
    PriorityPickerPeer.updateChoices(getPickerOptions("Priority"));
    TagsTypeaheadPeer.updateChoices(getPickerOptions("Tag"));
    TeamPickerPeer.updateChoices(getPickerOptions(TEMPLATE_DROPDOWN_RETURN_VALUE.TEAM));
    GroupPickerPeer.updateChoices(getPickerOptions(TEMPLATE_DROPDOWN_RETURN_VALUE.GROUP));
}

export async function initializeCustomPropertiesState(currentState: AppStateValue, setAppState: any) {
    const api = new platformService();
    const results = await Promise.all([
        api.getCustomProperties(`?${FILTERS_STRINGS.CUSTOM_PROPERTY_LIST}&$orderby=Order`),
        api.getDataSources("?$filter=EntityType eq 'CustomProperty'&$expand=CustomProperty&$orderby=Name")
    ]);
    const customProperties: ICustomProperty[] = results[0]?.data?.value ?? [];
    currentState.customProperties = customProperties;
    setAppState((prev) => ({
        ...prev,
        customProperties: customProperties
    }));

    const dsData: IDataSource[] = results[1]?.data?.value ?? [];
    return dsData;
}

export const formatTimeInput = (timeValue: string) => {
  const leadingZero = (num) => `0${num}`.slice(-2);
  const localDateTime = new Date(timeValue);
  const formatTime = [localDateTime.getHours(), localDateTime.getMinutes()]
    .map(leadingZero)
    .join(':');

  return formatTime;
}

export const formatDateInput = (dateValue: string) => {
  //saved card answer for dates is now in this format "yyyy-MM-dd'T'HH:mm:ss.fffzzz"; This includes user timezone.
  //saved card answer for dates previously in this format "yyyy-MM-dd"; No user timezone.
  const datePartOnly = dateValue?.split("T")[0] ?? "";
  return datePartOnly;
}

// The form designer stores a field's default in the element's `value`. parseCard's ticket-field
// and list-property branches read only the answers, so on a form with no answers yet that default
// was overwritten with undefined (and then recorded as "") before the form was shown or sent.
// Seed the answer from it instead. An answer that exists - even an empty one - still wins.
export const seedDesignerDefault = (item: { id?: string; value?: unknown }, data: Record<string, unknown>) => {
  if (!item.id || Object.prototype.hasOwnProperty.call(data, item.id)) return;
  const value = item.value;
  if (value == null || value === '' || value === `\${${item.id}}`) return;
  data[item.id] = value;
};

// A number input's designer default is serialized as a JSON number, while answers can arrive as
// numbers or strings. Checking `.length` alone treated every numeric default as absent.
export const toNumberInputValue = (value: unknown): number | '' => {
  if (typeof value === 'number') return isFinite(value) ? value : '';
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? '' : parsed;
  }
  return '';
};

function traverseChildNode(props: TicketProps[], parentId: number, level: number, setPrefix: boolean = true){
    const repeat = level * 2;
    let arrangedOptions = [];
    const childNodes = props.filter(p => p.ParentId == parentId);
    childNodes.sort((a, b) => a.Position - b.Position);
    childNodes.forEach(option => {
        option.Value = setPrefix ? `${"-".repeat(repeat)} ${option.Value}` : option.Value;
        arrangedOptions.push(option);
        const childNodes = traverseChildNode(props, option.Id, ++level, setPrefix);
        if(childNodes.length > 0)
            arrangedOptions = arrangedOptions.concat(childNodes);
    });
    return arrangedOptions;
}

function countParentLevels(props: TicketProps[], parentId: number, level: number){
    const item = props.find(p => p.Id == parentId);
    if(item.ParentId != null)
        level = countParentLevels(props, item.ParentId, level+1);
    return level;
}

export function mapPickerTreeNode(props: TicketProps[], appStateProps?: TicketProps[]){
    let arrangedOptions = [];
    let options = appStateProps ?? props;
    let sortedOptions = [];
    const childNodes = traverseChildNode(options, null, 1, false);
    if(childNodes.length > 0)
        sortedOptions = sortedOptions.concat(childNodes);
    sortedOptions.forEach(option => {
        const originalOption = props.find(p => p.Id == option.Id);
        if(originalOption){
            if(originalOption.ParentId == null){
                arrangedOptions.push(originalOption);
                const childNodes = traverseChildNode(props, option.Id, 1);
                if(childNodes.length > 0)
                    arrangedOptions = arrangedOptions.concat(childNodes);
            }else if(!props.some(p => p.Id == originalOption.ParentId)){
                arrangedOptions.push(originalOption);
            }
        }
    });
    return arrangedOptions;
}

export function mapCardEnumProperties(props: any[], valueField: string = "Id", labelField: string = "Value") {
    return props?.map(s => {
        return {
            "title": s[labelField]?.toString() ?? "",
            "value": s[valueField]?.toString() ?? ""
        } as AdaptiveCards.Choice
    }) ?? [];
}

export function removeOtherPropertyConfigurations(propertySheet: PropertySheet) {

    const props: PropertySheetEntry[] = propertySheet["_categories"][PropertySheetCategory.DefaultCategory]["_entries"];
    const layoutProps: PropertySheetEntry[] = propertySheet["_categories"][PropertySheetCategory.LayoutCategory]["_entries"];

    props.concat(layoutProps).forEach(p => {
        propertySheet.remove(p);
    });
}

export function SetTeamsExperiencePropertyConfigurations(propertySheet: PropertySheet, entries: PropertySheetEntry[]) {

    propertySheet.add(
        "Teams Experience",
        ...entries
    );
}

export function GetExpandedInputChoiceSet(parent: any, isMultiSelect: boolean = true) {
    let choiceSetInput = new AdaptiveCards.ChoiceSetInput();
    choiceSetInput.setParent(parent);
    choiceSetInput.style = "expanded";
    choiceSetInput.isMultiSelect = isMultiSelect;
    const defaultChoices: AdaptiveCards.Choice[] = [
        { title: "Choice 1", value: "1" } as AdaptiveCards.Choice,
        { title: "Choice 2", value: "2" } as AdaptiveCards.Choice
    ];
    choiceSetInput.choices = defaultChoices;

    const renderedElement =  choiceSetInput.render();
    parent.inputControlContainerElement.style.display = "block";

    return renderedElement;
}

export interface CreateCustomElementsProps {
    container: any[];
    currentState: AppStateValue;
    onInputValueChanged: (input: Input) => void;
    uploadFileProps: AdaptiveCardUploadProps;
    requestMappedText: string;
    isDisabled: boolean;
    setOnLoadUpdatedInputs: any;
    showDefaultCardButtons: boolean;
    applyCustomVisibility: boolean;
    isEndUserPage: boolean;
}

export const createCustomElements = (props: CreateCustomElementsProps) => {
    const errorMessage = AdaptiveCardTexts.REQUIRE_FIELD; 
    const {container, currentState, onInputValueChanged, uploadFileProps, requestMappedText, isDisabled, setOnLoadUpdatedInputs, showDefaultCardButtons, applyCustomVisibility, isEndUserPage} = props;
    container?.forEach(item => {
      if ((item.type === 'Container' || item.type === 'Column') && item.items) {
        const newProps = {...props, container: item.items};
        createCustomElements(newProps);
      } else if (item.type === 'ColumnSet' && item.columns) {
        const newProps = {...props, container: item.columns};
        createCustomElements(newProps);
      } else if (item.type == CardPeoplePicker.JsonTypeName) {
        CardPeoplePicker.onCustomValueChange = onInputValueChanged;
        CardPeoplePicker.disableField = isDisabled;

        if(item.displayLabel){
            item.label = item.displayLabel;
            delete item.displayLabel;
        }
      } else if (item.id?.startsWith('Ticket.')) {
        let replaceParentLabel = true;

        switch(item.id){
            case RequestTitle.ElementId:
                item.type = "Input.Text";
                item.isMultiline = true;
                item.isRequired = true;
                item.errorMessage = errorMessage;
                if(item.visibleField != undefined){
                    item.isVisible = item.visibleField;
                    delete item.visibleField;
                }
                
                if(item.contentField && requestMappedText && (item.contentField == item.value)){
                    item.contentField = item.contentField.replace(/\{{message\}}/ig, requestMappedText);
                    item.value = item.contentField.trim().length == 0 ? "" : item.contentField;
                    delete item.contentField;
                }
                
                setOnLoadUpdatedInputs(prevState => {
                    prevState[RequestTitle.ElementId] = item.value;
                    return prevState;
                });
                break;
            case StatusPicker.ElementId:
            case TicketTypePicker.ElementId:
            case CategoryPicker.ElementId:
            case PriorityPicker.ElementId:
                const keyValue = {};
                keyValue[StatusPicker.ElementId] = _.cloneDeep(currentState?.ticketStatus ?? []);
                keyValue[TicketTypePicker.ElementId] = _.cloneDeep(currentState?.ticketTypes ?? []);
                keyValue[CategoryPicker.ElementId] = _.cloneDeep(currentState?.categories ?? []);
                keyValue[PriorityPicker.ElementId] = _.cloneDeep(currentState?.priority ?? []);
                item.type = "Input.ChoiceSet";
                if(!item.choices){
                    const choicesSource = mapPickerTreeNode(keyValue[item.id]);
                    item.choices = mapCardEnumProperties(choicesSource);
                    const defaultChoice = choicesSource.find(x => x["IsDefault"]);
                    if(defaultChoice && !item.value && !item.requireField)
                        item.value = `${defaultChoice["Id"]}`;
                }
                break;
            case TagsTypeahead.ElementId:
                TagsTypeahead.pickerChoices = item.choices ? item.choices 
                : mapCardEnumProperties(currentState?.tags ?? [], "Id", "Name");
                TagsTypeahead.onCustomValueChange = onInputValueChanged;
                TagsTypeahead.disableField = isDisabled;
                TagsTypeahead.presentInTeams = false;
                break;
            case CollaboratorsTypeahead.ElementId:
                CollaboratorsTypeahead.pickerChoices = currentState?.usersInRole.filter(f => f.Id > 0).map(s => {
                    return {
                        "label": s.FullName,
                        "value": s.Id.toString()
                    }
                });
                CollaboratorsTypeahead.onCustomValueChange = onInputValueChanged;
                CollaboratorsTypeahead.disableField = isDisabled;
                CollaboratorsTypeahead.presentInTeams = false;
                break;
            case TeamPicker.ElementId:
                item.type = "Input.ChoiceSet";
                if(!item.choices){
                    const allTeams = currentState?.teams.filter(t => (t["TeamsAadObjectId"]?.length ?? 0) > 0);
                    item.choices = mapCardEnumProperties(allTeams, "Id", "FriendlyName");
                    const defaultChoice = allTeams.find(x => x["IsDefault"]);
                    if(defaultChoice && !item.value && !item.requireField)
                        item.value = `${defaultChoice["Id"]}`;
                }
                break;
            case GroupPicker.ElementId:
                item.type = "Input.ChoiceSet";
                if(!item.choices)
                    item.choices = mapCardEnumProperties(currentState?.supportgroups, "Id", "Name");
                break;
            case Assignment.ElementId:
                const teams = currentState?.teams ?? [];
                const groups = currentState?.supportgroups ?? [];
                
                Assignment.teamChoices = teams;
                Assignment.groupChoices = groups;

                let usersArr = currentState?.usersInRole ?? [];
                const team = teams.find(g => g.TeamsAadObjectId == item.team);
                if(item.team && team?.Users)
                    usersArr = team?.Users ?? [];
                if(item.group && item.group != "-1"){
                    let selectedGroup = groups.find(g => g.Id.toString() == item.group);
                    if(item.group == "0" && team){
                        selectedGroup = groups.find(g => g.TeamsChannelId == team.TeamsChannelId);
                        Assignment.defaultGroup = selectedGroup;
                    }
                    if(selectedGroup?.Members)
                        usersArr = selectedGroup?.Members ?? [];
                }

                Assignment.displayChoices = filterPlatformUsersInRole(usersArr, currentState.usersInRole).map(s => {
                    return {
                        "title": s.FullName,
                        "value": s.Id.toString()
                    }
                });
                break;
            case DueDate.ElementId:
            case ResolutionDate.ElementId:
                item.type = "Input.Date";
                if((item?.value ?? "").length > 0)
                    item.value = formatDateInput(item.value);
                break;
            case UploadFile.ElementId:
                replaceParentLabel = false;
                if(uploadFileProps){
                    UploadFile.onOpenUploadFromCardAction = uploadFileProps.onOpenUploadFromCardAction;
                    UploadFile.filesToUpload = uploadFileProps.currentFiles;
                    UploadFile.message = uploadFileProps.message;
                    UploadFile.messageIsPrivate = uploadFileProps.messageIsPrivate;
                    UploadFile.disableField = isDisabled;
                }
                break;
            case AddComment.ElementId:
                replaceParentLabel = false;
                AddComment.isEndUserPage = isEndUserPage;
                if(item.visibleField != undefined){
                    item.isVisible = item.visibleField;
                    delete item.visibleField;
                }

                let commentValue = JSON.parse(item.value || "{}") as Comment;
                if(item.contentField && requestMappedText && (item.contentField == commentValue.Body)){
                    item.contentField = item.contentField.replace(/\{{message\}}/ig, requestMappedText);
                    const newValue = item.contentField.trim().length == 0 ? "" : item.contentField;
                    commentValue.Body = newValue;
                    item.value = JSON.stringify(commentValue);
                    delete item.contentField;
                }

                setOnLoadUpdatedInputs(prevState => {
                    prevState[AddComment.ElementId] = item.value;
                    return prevState;
                });
                break;
            case AffectedUsersPeoplePicker.ElementId:
                AffectedUsersPeoplePicker.onCustomValueChange = onInputValueChanged;
                AffectedUsersPeoplePicker.disableField = isDisabled;
                break;
            case RequesterPeoplePicker.ElementId:
                RequesterPeoplePicker.onCustomValueChange = onInputValueChanged;
                RequesterPeoplePicker.disableField = isDisabled;
                break;
            case SubmitRequest.ElementId:
                SubmitRequest.hideField = !showDefaultCardButtons;
                replaceParentLabel = false;
                break;
            case StatusOfMyRequests.ElementId:
                StatusOfMyRequests.hideField = !showDefaultCardButtons;
                replaceParentLabel = false;
                break;
            default: 
                break;
        }
        
        if(item.displayLabel && replaceParentLabel){
            item.label = item.displayLabel;
            delete item.displayLabel;
        }

        if(item.requireField){
            item.isRequired = item.requireField;
            item.errorMessage = errorMessage;
            delete item.requireField;
        }
      } else if (item.isCustomProperty && item.dataType) {
        const cp = currentState?.customProperties?.find(cp => cp.PropertyId === item.id);
        if(!cp || cp.IsHidden || cp.IsArchived)
            item.isVisible = false;
        let replaceParentLabel = true;
        switch(CustomPropertyDataType_LABELS[item.dataType]){
            case CustomPropertyDataType_LABELS.INT:
                item.type = "Input.Number";
                break;
            case CustomPropertyDataType_LABELS.BOOL:
                item.type = "Input.Toggle";
                if(item.displayTitle){
                    item.title = item.displayTitle;
                    delete item.displayTitle;
                }
                break;
            case CustomPropertyDataType_LABELS.DATE:
                item.type = "Input.Date";
                if((item?.value ?? "").length > 0 && item.value.indexOf('T') > -1) 
                    item.value = formatDateInput(item.value);
                break;
            case CustomPropertyDataType_LABELS.TIME:
                item.type = "Input.Time";
                if((item?.value ?? "").length > 0 && item.value.indexOf('T') > -1)                
                    item.value = formatTimeInput(item.value);
                break;
            case CustomPropertyDataType_LABELS.LIST:
                if(!item.isMultiSelectField){
                    item.type = "Input.ChoiceSet";
                    const propertyData = currentState?.customProperties.find(p => p.PropertyId == item.id);
                    const nestedList = _.cloneDeep(propertyData?.CustomPropertyListItems?.filter(pItem => !pItem.IsArchived && !pItem.IsDeleted) ?? []);
                    if(!item.choices){
                        const choicesSource = mapPickerTreeNode(nestedList as TicketProps[]);
                        item.choices = mapCardEnumProperties(choicesSource);
                        const defaultChoice = choicesSource.find(x => x["IsDefault"]);
                        if(defaultChoice && !item.value && !item.requireField)
                            item.value = `${defaultChoice["Id"]}`;
                    }
                }else{
                    ListProperty.onCustomValueChange = onInputValueChanged;
                    ListProperty.disableField = isDisabled;
                    ListProperty.presentInTeams = false;
                    ListProperty.currentState = currentState;
                }
                break;
            default:
                item.type = "Input.Text";
                break;
        }

        if(item.displayLabel && replaceParentLabel){
            item.label = item.displayLabel;
            delete item.displayLabel;
        }

        if(item.requireField){
            item.isRequired = item.requireField;
            if((item.errorMessage?.length ?? 0) == 0)
                item.errorMessage = errorMessage;
            delete item.requireField;
        }
      }

      if(item.hasOwnProperty("isVisible") && !item.isVisible && applyCustomVisibility && !item.isCustomProperty)
        item.isVisible = true;

      if(item.hasOwnProperty("isVisible") && !item.isVisible && item.hasOwnProperty("isRequired") && item.isRequired)
        item.isRequired = false;
    });
}

export const handleValidationElement = (renderedElement: HTMLElement) => {
    const validationFailed = renderedElement.className.includes('ac-input-validation-failed');
    if(validationFailed){
        renderedElement.classList.remove('ac-input-validation-failed');
        let labels = renderedElement.getAttribute("aria-labelledby");
        const labelsArr = labels.split(/\s+/);
        labelsArr.forEach(label => {
            const checkEl = document.getElementById(label);
            if(checkEl?.className.includes("ac-textBlock")){
                checkEl.hidden = true;
            }
        });

    }
}

export const initCustomRenderedElement = (className: string = "tikit-input") => {
    let renderedElement = document.createElement("div");
    renderedElement.setAttribute("style", "width: 100%");
    const classes = className.split(/\s+/);
    classes.forEach(c => {
        renderedElement.classList.add(c);
    });

    return renderedElement;
}